import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  startOfWeek,
  startOfMonth,
  getDaysInMonth,
  addDays,
  addMonths,
  parseISO,
  differenceInCalendarDays,
  isSameDay,
  format,
} from "date-fns";
import {
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Loader2,
  CalendarDays,
  Plus,
  Sparkles,
  Home,
} from "lucide-react";

const DAY_W = 56; // px per day column
const LABEL_W = 208; // px property label column
const ROW_H = 60; // px per property row

const RANGE = { week: 7, "2week": 14, month: 30 };

const emptyForm = {
  property_id: "",
  date: "",
  assigned_cleaner_ids: [],
  notes: "",
  cost_per_cleaning: "",
  payouts: {},
};

export default function CalendarPage() {
  const [reservations, setReservations] = useState([]);
  const [properties, setProperties] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [cleaners, setCleaners] = useState([]);
  const [propFilter, setPropFilter] = useState("all");
  const [view, setView] = useState("2week");
  const [cursor, setCursor] = useState(() => new Date());
  const [syncing, setSyncing] = useState(false);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const load = () => api.get("/reservations").then((r) => setReservations(r.data));
  const loadJobs = () => api.get("/jobs").then((r) => setJobs(r.data));
  useEffect(() => {
    load();
    loadJobs();
    api.get("/properties").then((r) => setProperties(r.data));
    api.get("/assignable").then((r) => setCleaners(r.data)).catch(() => {});
  }, []);

  const toggleCleaner = (id) =>
    setForm((f) => {
      const has = f.assigned_cleaner_ids.includes(id);
      const ids = has
        ? f.assigned_cleaner_ids.filter((c) => c !== id)
        : [...f.assigned_cleaner_ids, id];
      const payouts = { ...f.payouts };
      if (has) delete payouts[id];
      return { ...f, assigned_cleaner_ids: ids, payouts };
    });

  const openNew = (propId, dateStr) => {
    setForm({ ...emptyForm, property_id: propId || "", date: dateStr || "" });
    setOpen(true);
  };

  const createAppt = async () => {
    if (!form.property_id || !form.date)
      return toast.error("Choose a property and date");
    setSaving(true);
    try {
      const payouts = {};
      Object.entries(form.payouts).forEach(([id, v]) => {
        if (v !== "" && v != null) payouts[id] = parseFloat(v) || 0;
      });
      await api.post("/jobs", {
        property_id: form.property_id,
        date: form.date,
        assigned_cleaner_ids: form.assigned_cleaner_ids,
        notes: form.notes,
        cost_per_cleaning:
          form.cost_per_cleaning === "" ? null : parseFloat(form.cost_per_cleaning) || 0,
        payouts,
      });
      toast.success("Appointment created");
      setOpen(false);
      setForm(emptyForm);
      loadJobs();
    } catch (e) {
      toast.error("Could not create appointment");
    } finally {
      setSaving(false);
    }
  };

  const syncAll = async () => {
    setSyncing(true);
    let total = 0;
    try {
      for (const p of properties) {
        if (p.ical_airbnb || p.ical_vrbo) {
          const { data } = await api.post(`/properties/${p.id}/sync`);
          total += data.synced;
        }
      }
      toast.success(`Synced ${total} new reservation(s)`);
      load();
      loadJobs();
    } catch (e) {
      toast.error("Some calendars failed to sync");
    } finally {
      setSyncing(false);
    }
  };

  const visibleProps =
    propFilter === "all" ? properties : properties.filter((p) => p.id === propFilter);

  // ---- date range ----
  const { rangeStart, days } = useMemo(() => {
    let start;
    let count;
    if (view === "month") {
      start = startOfMonth(cursor);
      count = getDaysInMonth(cursor);
    } else {
      start = startOfWeek(cursor, { weekStartsOn: 0 });
      count = RANGE[view];
    }
    const arr = [];
    for (let i = 0; i < count; i++) arr.push(addDays(start, i));
    return { rangeStart: start, days: arr };
  }, [cursor, view]);

  const totalDays = days.length;
  const trackW = totalDays * DAY_W;
  const today = new Date();

  const navigate = (dir) => {
    if (view === "month") setCursor((c) => addMonths(c, dir));
    else setCursor((c) => addDays(c, dir * RANGE[view]));
  };

  const rangeLabel = `${format(days[0], "MMM d")} – ${format(
    days[totalDays - 1],
    "MMM d, yyyy"
  )}`;

  // ---- bar geometry for a reservation ----
  const barGeom = (ci, co) => {
    const startIdx = differenceInCalendarDays(parseISO(ci), rangeStart);
    const endIdx = differenceInCalendarDays(parseISO(co), rangeStart);
    if (endIdx <= 0 || startIdx >= totalDays) return null; // fully outside
    const leftPx = startIdx >= 0 ? startIdx * DAY_W + DAY_W * 0.5 : 0;
    const rightPx = endIdx <= totalDays ? endIdx * DAY_W + DAY_W * 0.5 : trackW;
    const w = rightPx - leftPx;
    if (w <= 6) return null;
    return {
      left: leftPx,
      width: w,
      clippedLeft: startIdx < 0,
      checkoutVisible: endIdx <= totalDays,
      checkoutPx: endIdx * DAY_W + DAY_W * 0.5,
    };
  };

  return (
    <div className="p-4 md:p-6 max-w-[1500px] mx-auto" data-testid="calendar-page">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-5">
        <div>
          <p className="text-xs tracking-[0.15em] uppercase font-bold text-primary mb-1">
            Reservations
          </p>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Calendar</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Tabs value={view} onValueChange={setView}>
            <TabsList data-testid="calendar-view-tabs">
              <TabsTrigger value="week" data-testid="view-week">Week</TabsTrigger>
              <TabsTrigger value="2week" data-testid="view-2week">2 Weeks</TabsTrigger>
              <TabsTrigger value="month" data-testid="view-month">Month</TabsTrigger>
            </TabsList>
          </Tabs>
          <Select value={propFilter} onValueChange={setPropFilter}>
            <SelectTrigger className="w-40" data-testid="calendar-property-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All properties</SelectItem>
              {properties.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={syncAll} disabled={syncing} data-testid="sync-all-button">
            {syncing ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Sync all
          </Button>
        </div>
      </div>

      {/* Date navigation */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-lg font-semibold" data-testid="calendar-range-label">
          {rangeLabel}
        </h2>
        <div className="flex gap-1">
          <Button variant="outline" size="icon" onClick={() => navigate(-1)} data-testid="cal-prev">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => setCursor(new Date())} data-testid="cal-today">
            Today
          </Button>
          <Button variant="outline" size="icon" onClick={() => navigate(1)} data-testid="cal-next">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-x-4 gap-y-1.5 mb-3 text-xs flex-wrap text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-3 w-5 rounded-full bg-primary" /> Guest stay
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-4 w-4 rounded-full bg-[#D9534F] flex items-center justify-center">
            <Sparkles className="h-2.5 w-2.5 text-white" />
          </span>
          Turnover clean
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-3 w-5 rounded-md border border-[#F5A623]/40 bg-[#F5A623]/15" /> Manual clean
        </span>
        <span>· Click an empty cell to add a cleaning</span>
      </div>

      {/* Timeline */}
      {visibleProps.length === 0 ? (
        <Card className="p-12 text-center">
          <Home className="h-10 w-10 text-muted-foreground mx-auto mb-4" strokeWidth={1.3} />
          <p className="text-muted-foreground">Add a property to see it on the calendar.</p>
        </Card>
      ) : (
        <Card className="overflow-hidden p-0">
          <div className="overflow-x-auto scrollbar-thin" data-testid="calendar-timeline">
            <div style={{ minWidth: LABEL_W + trackW }}>
              {/* Header row */}
              <div className="flex border-b border-border bg-background sticky top-0 z-20">
                <div
                  className="sticky left-0 z-30 bg-background border-r border-border flex items-center px-4 shrink-0"
                  style={{ width: LABEL_W, height: 48 }}
                >
                  <span className="text-xs font-semibold tracking-[0.05em] uppercase text-muted-foreground">
                    Property
                  </span>
                </div>
                {days.map((d, i) => {
                  const isToday = isSameDay(d, today);
                  const weekend = d.getDay() === 0 || d.getDay() === 6;
                  return (
                    <div
                      key={i}
                      className={`shrink-0 text-center border-r border-border/60 flex flex-col items-center justify-center ${
                        weekend ? "bg-[#F3F6F4]" : ""
                      } ${isToday ? "bg-[#E8F0EB]" : ""}`}
                      style={{ width: DAY_W, height: 48 }}
                    >
                      <span className="text-[10px] uppercase text-muted-foreground leading-none">
                        {format(d, "EEE")}
                      </span>
                      <span
                        className={`text-sm leading-tight ${
                          isToday ? "font-bold text-primary" : "font-medium text-foreground"
                        }`}
                      >
                        {format(d, "d")}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Property rows */}
              {visibleProps.map((p) => {
                const color = p.color || "#4A7C59";
                const resv = reservations.filter((r) => r.property_id === p.id);
                const manual = jobs.filter(
                  (j) => j.source === "manual" && j.property_id === p.id
                );
                return (
                  <div key={p.id} className="flex border-b border-border/60 group" data-testid={`cal-row-${p.id}`}>
                    {/* Label */}
                    <div
                      className="sticky left-0 z-20 bg-card group-hover:bg-slate-50 border-r border-border flex items-center gap-2 px-4 shrink-0"
                      style={{ width: LABEL_W, height: ROW_H }}
                    >
                      <span
                        className="h-7 w-1.5 rounded-full shrink-0"
                        style={{ backgroundColor: color }}
                      />
                      <span className="text-sm font-medium truncate">{p.name}</span>
                    </div>

                    {/* Track */}
                    <div className="relative shrink-0" style={{ width: trackW, height: ROW_H }}>
                      {/* background day cells */}
                      <div className="absolute inset-0 flex">
                        {days.map((d, i) => {
                          const weekend = d.getDay() === 0 || d.getDay() === 6;
                          const isToday = isSameDay(d, today);
                          const dateStr = format(d, "yyyy-MM-dd");
                          return (
                            <button
                              key={i}
                              onClick={() => openNew(p.id, dateStr)}
                              title={`Add cleaning · ${p.name} · ${dateStr}`}
                              className={`shrink-0 border-r border-border/40 hover:bg-primary/10 transition-colors ${
                                weekend ? "bg-[#F3F6F4]/60" : ""
                              } ${isToday ? "bg-[#E8F0EB]/60" : ""}`}
                              style={{ width: DAY_W, height: ROW_H }}
                              data-testid={`cal-cell-${p.id}-${dateStr}`}
                            />
                          );
                        })}
                      </div>

                      {/* reservation bars */}
                      {resv.map((r) => {
                        const g = barGeom(r.check_in, r.check_out);
                        if (!g) return null;
                        return (
                          <div
                            key={r.id}
                            className="absolute flex items-center text-white text-[11px] font-medium px-2.5 shadow-sm overflow-hidden z-10 pointer-events-none"
                            style={{
                              left: g.left,
                              width: g.width,
                              top: 10,
                              height: 26,
                              backgroundColor: color,
                              borderRadius: 13,
                              borderTopLeftRadius: g.clippedLeft ? 0 : 13,
                              borderBottomLeftRadius: g.clippedLeft ? 0 : 13,
                            }}
                            title={`${p.name} · ${r.check_in} → ${r.check_out} (${r.source})`}
                            data-testid={`cal-resv-${r.id}`}
                          >
                            <span className="truncate capitalize">{r.source} stay</span>
                          </div>
                        );
                      })}

                      {/* turnover markers (checkout day of each reservation) */}
                      {resv.map((r) => {
                        const g = barGeom(r.check_in, r.check_out);
                        if (!g || !g.checkoutVisible) return null;
                        return (
                          <div
                            key={`t-${r.id}`}
                            className="absolute z-20 rounded-full bg-[#D9534F] border-2 border-white flex items-center justify-center shadow"
                            style={{
                              left: g.checkoutPx - 9,
                              top: 14,
                              width: 18,
                              height: 18,
                            }}
                            title={`Turnover clean · ${p.name} · ${r.check_out}`}
                            data-testid={`cal-turnover-${r.id}`}
                          >
                            <Sparkles className="h-2.5 w-2.5 text-white" />
                          </div>
                        );
                      })}

                      {/* manual cleaning chips */}
                      {manual.map((j) => {
                        const idx = differenceInCalendarDays(parseISO(j.date), rangeStart);
                        if (idx < 0 || idx >= totalDays) return null;
                        return (
                          <div
                            key={j.id}
                            className="absolute z-10 rounded-md border border-[#F5A623]/40 bg-[#F5A623]/15 text-[#B26A00] text-[9px] font-bold uppercase flex items-center justify-center px-1 overflow-hidden pointer-events-none"
                            style={{
                              left: idx * DAY_W + 3,
                              width: DAY_W - 6,
                              bottom: 5,
                              height: 16,
                            }}
                            title={`Manual clean · ${p.name} · ${j.date}`}
                            data-testid={`cal-manual-${j.id}`}
                          >
                            Clean
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </Card>
      )}

      {/* New manual appointment dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">New cleaning appointment</DialogTitle>
            <DialogDescription>
              Schedule a manual clean for a property on a specific date.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Property</Label>
              <Select
                value={form.property_id}
                onValueChange={(v) => setForm({ ...form, property_id: v })}
              >
                <SelectTrigger data-testid="appt-property-select">
                  <SelectValue placeholder="Choose a property" />
                </SelectTrigger>
                <SelectContent>
                  {properties.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Date</Label>
              <Input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                data-testid="appt-date-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Payment to company ($)</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                placeholder="e.g. 150"
                value={form.cost_per_cleaning}
                onChange={(e) => setForm({ ...form, cost_per_cleaning: e.target.value })}
                data-testid="appt-payment-input"
              />
              <p className="text-xs text-muted-foreground">
                What the client pays for this clean. Leave blank to use the property default.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Assign cleaners & payout</Label>
              {cleaners.length === 0 && (
                <p className="text-xs text-muted-foreground">No cleaners on your team yet.</p>
              )}
              <div className="space-y-2">
                {cleaners.map((c) => {
                  const selected = form.assigned_cleaner_ids.includes(c.id);
                  return (
                    <div
                      key={c.id}
                      className="flex items-center gap-3 rounded-lg border border-border p-2.5"
                    >
                      <Checkbox
                        checked={selected}
                        onCheckedChange={() => toggleCleaner(c.id)}
                        data-testid={`appt-cleaner-${c.id}`}
                      />
                      <span className="flex-1 text-sm">{c.name}</span>
                      {selected && (
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-muted-foreground">payout $</span>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            className="h-8 w-24"
                            placeholder="0"
                            value={form.payouts[c.id] ?? ""}
                            onChange={(e) =>
                              setForm((f) => ({
                                ...f,
                                payouts: { ...f.payouts, [c.id]: e.target.value },
                              }))
                            }
                            data-testid={`appt-payout-${c.id}`}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Input
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Gate code, special instructions…"
                data-testid="appt-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={createAppt} disabled={saving} data-testid="appt-save-button">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create appointment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
